import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useLoginModal } from '../LoginModalContext'
import gamesIcon from '../../images/spil.png'
import holdIcon from '../../images/hold-ball-nav.png'
import { useTeamState } from '../../pages/Fodboldhold/store'
import './BottomNavigation.css'

const GAME_ACCESS = ['yatzy', 'championsLeague', 'worldCup2026', 'landskamp', 'kampdag']

const GAME_PREFIXES = [
  '/yatzy',
  '/champions-league',
  '/world-cup-2026',
  '/landskamp',
  '/kampdag',
]

const HOLD_NAV_KEY = 'svirrens-hold-nav'

function rememberedHold(userId) {
  if (!userId) return false
  try {
    return localStorage.getItem(HOLD_NAV_KEY) === userId
  } catch {
    return false
  }
}

function rememberHold(userId, onTeam) {
  if (!userId) return
  try {
    if (onTeam) localStorage.setItem(HOLD_NAV_KEY, userId)
    else if (localStorage.getItem(HOLD_NAV_KEY) === userId) localStorage.removeItem(HOLD_NAV_KEY)
  } catch {
    // Privat tilstand kan blokere lageret.
  }
}

function HomeIcon({ filled }) {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M4.2 10.7 12 4l7.8 6.7V20a1.2 1.2 0 0 1-1.2 1.2h-4.3v-6.1H9.7v6.1H5.4A1.2 1.2 0 0 1 4.2 20v-9.3Z"
        fill={filled ? 'currentColor' : 'none'}
        stroke="currentColor"
        strokeWidth={filled ? 0 : 1.8}
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  )
}

function ProfileIcon({ filled }) {
  if (filled) {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <circle cx="12" cy="8" r="3.2" fill="currentColor" />
        <path
          fill="currentColor"
          d="M5.2 19.3c1.15-3.15 3.55-4.8 6.8-4.8s5.65 1.65 6.8 4.8c.25.65-.25 1.3-.95 1.3H6.15c-.7 0-1.2-.65-.95-1.3z"
        />
      </svg>
    )
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="12" cy="8" r="3.1" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path
        d="M5.6 19.5c1.25-3 3.6-4.6 6.4-4.6s5.15 1.6 6.4 4.6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  )
}


function AdminIcon({ filled }) {
  if (filled) {
    return (
      <svg viewBox="0 0 24 24" aria-hidden="true">
        <path
          fill="currentColor"
          fillRule="evenodd"
          d="M12 3.2 19.4 6.4v5.4c0 4.7-3 7.9-7.4 9.5-4.4-1.6-7.4-4.8-7.4-9.5V6.4L12 3.2Zm0 6.2a1.7 1.7 0 0 0-.9 3.1v2.5h1.8v-2.5A1.7 1.7 0 0 0 12 9.4Z"
        />
      </svg>
    )
  }

  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M12 3.4 19 6.5v5.2c0 4.5-2.8 7.6-7 9.1-4.2-1.5-7-4.6-7-9.1V6.5L12 3.4Z"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="11" r="1.45" fill="none" stroke="currentColor" strokeWidth="1.6" />
      <path d="M12 12.3v2.5" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}

function BottomNavigation() {
  const location = useLocation()
  const navigate = useNavigate()
  const { openLogin } = useLoginModal()
  const user = useSelector((state) => state.mate.currentUser)
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated)
  const token = useSelector((state) => state.auth.token)
  const isLoggedIn = isAuthenticated && !!token && !!user
  const isAdmin = isLoggedIn && user?.role === 'admin'
  const hasGames = isAdmin || (isLoggedIn && (user?.pageAccess || []).some((key) => GAME_ACCESS.includes(key)))
  const teamState = useTeamState()
  const teams = teamState?.state.teams || []
  const hasTeam = teams.some((team) => team.members.some((member) => member.id === teamState.meId))
  const teamsLoading = Boolean(teamState?.state.loading)
  const teamsUnknown = teamsLoading || (Boolean(teamState?.state.error) && teams.length === 0)
  const showHold = hasTeam || (teamsUnknown && rememberedHold(user?.id))

  useEffect(() => {
    if (!user?.id || teamsLoading || (teamState?.state.error && teams.length === 0)) return
    rememberHold(user.id, hasTeam)
  }, [hasTeam, teamState?.state.error, teams.length, teamsLoading, user?.id])

  const path = location.pathname
  const onProfile = path === '/profile' || path.startsWith('/profile/')
  const onAdmin = path === '/admin' || path.startsWith('/admin/')
  const onHold = path === '/hold' || path.startsWith('/hold/')
  const inGame = path === '/spil' || GAME_PREFIXES.some(
    (prefix) => path === prefix || path.startsWith(`${prefix}/`)
  )
  const [selected, setSelected] = useState('home')

  useEffect(() => {
    if (onAdmin) {
      setSelected('admin')
      return
    }
    if (onProfile) {
      setSelected('profile')
      return
    }
    if (onHold) {
      setSelected('hold')
      return
    }
    if (inGame) {
      setSelected('games')
      return
    }
    if (path === '/') {
      setSelected('home')
      return
    }
    setSelected(null)
  }, [inGame, onAdmin, onHold, onProfile, path])

  const openProfile = () => {
    if (isLoggedIn) {
      setSelected('profile')
      navigate('/profile')
      return
    }
    openLogin()
  }

  return (
    <nav className="bottom-nav" aria-label="Bundnavigation">
      <Link
        to="/"
        className={`bottom-nav-item${selected === 'home' ? ' is-active' : ''}`}
        aria-label="Home"
        aria-current={selected === 'home' ? 'page' : undefined}
        onClick={() => setSelected('home')}
      >
        <span className="bottom-nav-icon">
          <HomeIcon filled={selected === 'home'} />
        </span>
      </Link>

      {hasGames ? (
        <Link
          to="/spil"
          className={`bottom-nav-item${selected === 'games' ? ' is-active' : ''}`}
          aria-label="Spil"
          aria-current={selected === 'games' ? 'page' : undefined}
          onClick={() => setSelected('games')}
        >
          <span className="bottom-nav-icon">
            <img className="bottom-nav-games-icon" src={gamesIcon} alt="" />
          </span>
        </Link>
      ) : null}

      {showHold ? (
        <Link
          to="/hold"
          className={`bottom-nav-item${selected === 'hold' ? ' is-active' : ''}`}
          aria-label="Hold"
          aria-current={selected === 'hold' ? 'page' : undefined}
          onClick={() => setSelected('hold')}
        >
          <span className="bottom-nav-icon">
            <img className="bottom-nav-hold-icon" src={holdIcon} alt="" />
          </span>
        </Link>
      ) : null}

      {isAdmin ? (
        <Link
          to="/admin"
          className={`bottom-nav-item${selected === 'admin' ? ' is-active' : ''}`}
          aria-label="Admin"
          aria-current={selected === 'admin' ? 'page' : undefined}
          onClick={() => setSelected('admin')}
        >
          <span className="bottom-nav-icon">
            <AdminIcon filled={selected === 'admin'} />
          </span>
        </Link>
      ) : null}

      <button
        type="button"
        className={`bottom-nav-item${selected === 'profile' ? ' is-active' : ''}`}
        aria-label="Profil"
        aria-current={selected === 'profile' ? 'page' : undefined}
        onClick={openProfile}
      >
        <span className="bottom-nav-icon">
          <ProfileIcon filled={selected === 'profile'} />
        </span>
      </button>
    </nav>
  )
}

export default BottomNavigation
